import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Paper,
  Typography,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getToken } from "../../../api/Account";
import { DataContext } from "../../../context/DataContext";

import { isAccess, simpleMessage } from "../../../helpers/Helpers";
import {
  getClientReferencesAsync,
  deleteClientReferenceAsync,
} from "../../../api/Clients";
import {
  CargandoTabla,
  ConfirmDialog,
  EstadoError,
  EstadoVacio,
  useAviso,
} from "../../../components/base";
import AddReference from "./referenceComponents/AddReference";
import EditReference from "./referenceComponents/EditReference";
import { ERROR_OTRA_SESION, encabezadoTabla } from "../comunes";

const FILAS_POR_PAGINA = 10;

const ClientReferences = ({ id }) => {
  const token = getToken();
  const [referenceList, setReferenceList] = useState([]);
  const [selectedReference, setSelectedReference] = useState([]);

  const [localReload, setLocalReload] = useState(false);

  const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
  const aviso = useAviso();

  // Estado local de la pestaña: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  const [pagina, setPagina] = useState(0);
  const [addReferenceModal, setAddReferenceModal] = useState(false);
  const [editReferenceModal, seteditReferenceModal] = useState(false);

  // Menú de tres puntos de una fila.
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  // Confirmación de eliminar. Se conserva la fila al cerrar para que el texto no cambie
  // durante la animación de salida.
  const [confirmacion, setConfirmacion] = useState({ abierto: false, fila: null });
  const [procesando, setProcesando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await getClientReferencesAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }
      setIsLoading(false);
      setReferenceList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar();
  }, [localReload]);

  // Si al eliminar la página actual queda fuera de rango, se vuelve a la primera.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= referenceList.length) {
      setPagina(0);
    }
  }, [pagina, referenceList.length]);

  const currentItem = referenceList.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const deleteReference = async (item) => {
    setIsLoading(true);
    try {
      const result = await deleteClientReferenceAsync(token, item.id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
          return;
        }
        aviso.error(result.error || "No se pudo eliminar la referencia");
        return;
      }

      setIsLoading(false);
      setLocalReload(!localReload);
      aviso.exito("Referencia eliminada");
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const pedirEliminar = (fila) => setConfirmacion({ abierto: true, fila });
  const cerrarConfirmacion = () =>
    setConfirmacion((actual) => ({ ...actual, abierto: false }));

  // Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
  const confirmar = async () => {
    setProcesando(true);
    try {
      await deleteReference(confirmacion.fila);
    } finally {
      setProcesando(false);
      cerrarConfirmacion();
    }
  };

  const puedeAgregar = isAccess(access, "CLIENTS CREATE");
  const puedeEditar = isAccess(access, "CLIENTS UPDATE");
  const puedeEliminar = isAccess(access, "CLIENTS DELETE");
  const mostrarAcciones = puedeEditar || puedeEliminar;
  const filaEliminar = confirmacion.fila;

  const botonAgregar = (
    <Button
      onClick={() => {
        setAddReferenceModal(true);
      }}
      variant="contained"
      startIcon={<AddIcon />}
    >
      Agregar referencia
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={4} columnas={5} etiqueta="Cargando las referencias…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar las referencias"
        }
        onReintentar={cargar}
      />
    );
  } else if (referenceList.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Sin referencias"
        descripcion="Las referencias personales son las personas que pueden dar razón del cliente. Aquí aparecerán las que agregues."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Referencias personales del cliente" size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Nombre</TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", md: "table-cell" } }}
                >
                  Dirección
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  Parentesco
                </TableCell>
                <TableCell
                  sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}
                >
                  Teléfono
                </TableCell>
                {mostrarAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.fullName}
                    </Typography>
                    {/* En el teléfono el parentesco y el teléfono pasan debajo del nombre. */}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", sm: "none" },
                      }}
                    >
                      {row.parentesco}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: { xs: "block", sm: "none" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.telefono}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    {row.direccion}
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    {row.parentesco}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    {row.telefono}
                  </TableCell>
                  {mostrarAcciones ? (
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {puedeEditar ? (
                        <Tooltip title="Cambiar referencia">
                          <IconButton
                            aria-label={`Cambiar la referencia de ${row.fullName}`}
                            onClick={() => {
                              setSelectedReference(row);
                              seteditReferenceModal(true);
                            }}
                            sx={{ color: "primary.main" }}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {puedeEliminar ? (
                        <Tooltip title="Más acciones">
                          <IconButton
                            aria-label={`Más acciones de la referencia de ${row.fullName}`}
                            aria-haspopup="menu"
                            onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={referenceList.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  return (
    <div>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent={"space-between"}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Typography
            component="h2"
            variant="h6"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            Referencias personales
          </Typography>

          {puedeAgregar ? botonAgregar : <></>}
        </Stack>

        {contenido}
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirEliminar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Eliminar</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        abierto={confirmacion.abierto}
        peligro
        cargando={procesando}
        titulo="Eliminar referencia"
        mensaje={
          filaEliminar ? (
            <>
              <Typography component="p" sx={{ mb: 1 }}>
                {`Se eliminará la referencia de ${filaEliminar.fullName}${
                  filaEliminar.parentesco ? ` (${filaEliminar.parentesco})` : ""
                } de este cliente.`}
              </Typography>
              <Typography component="p" sx={{ color: "text.secondary" }}>
                Esta acción no se puede deshacer.
              </Typography>
            </>
          ) : null
        }
        textoConfirmar="Eliminar referencia"
        onConfirmar={confirmar}
        onCancelar={cerrarConfirmacion}
      />

      <AddReference
        abierto={addReferenceModal}
        setShowModal={setAddReferenceModal}
        clientId={id}
        localReload={localReload}
        setLocalReload={setLocalReload}
      />

      <EditReference
        abierto={editReferenceModal}
        setShowModal={seteditReferenceModal}
        selectedReference={selectedReference}
        setSelectedReference={setSelectedReference}
        localReload={localReload}
        setLocalReload={setLocalReload}
      />
    </div>
  );
};

export default ClientReferences;
